import React from 'react';
import { Card, Statistic, Tooltip } from 'antd';
import {
  CalendarOutlined,
  FieldTimeOutlined,
  WarningOutlined,
  LogoutOutlined,
  PlusCircleOutlined,
  MinusCircleOutlined,
  CarOutlined,
} from '@ant-design/icons';

const METRICS = [
  {
    key: 'expected',
    title: 'موظفی',
    valueKey: 'expected_hours',
    decimalKey: 'expected_hours_decimal',
    preferHoursLabel: true,
    icon: <CalendarOutlined />,
    color: '#2563EB',
    bg: '#EFF6FF',
  },
  {
    key: 'present',
    title: 'حضور',
    valueKey: 'present_hours',
    decimalKey: 'present_hours_decimal',
    preferHoursLabel: true,
    icon: <FieldTimeOutlined />,
    color: '#16A34A',
    bg: '#F0FDF4',
  },
  {
    key: 'late',
    title: 'تأخیر',
    valueKey: 'late_time',
    icon: <WarningOutlined />,
    color: '#92400e',
    bg: '#fef3c7',
  },
  {
    key: 'early',
    title: 'تعجیل',
    valueKey: 'early_leave_time',
    icon: <LogoutOutlined />,
    color: '#c2410c',
    bg: '#fff7ed',
  },
  {
    key: 'overtime',
    title: 'اضافه‌کاری',
    valueKey: 'overtime',
    icon: <PlusCircleOutlined />,
    color: '#9333EA',
    bg: '#FAF5FF',
  },
  {
    key: 'deficit',
    title: 'کسری',
    valueKey: 'deficit_time',
    icon: <MinusCircleOutlined />,
    color: '#DC2626',
    bg: '#FEF2F2',
  },
  {
    key: 'mission',
    title: 'مأموریت',
    valueKey: 'mission_time',
    fallbackValueKey: 'mission_days',
    fallbackSuffix: ' روز',
    icon: <CarOutlined />,
    color: '#3730a3',
    bg: '#eef2ff',
    optional: true,
  },
];

const CORE_SUMMARY_KEYS = [
  'expected_hours',
  'present_hours',
  'late_time',
  'early_leave_time',
  'overtime',
  'deficit_time',
];

const SUMMARY_FIELD_KEYS = [
  ...CORE_SUMMARY_KEYS,
  'mission_time',
  'mission_days',
  'mission_minutes',
];

/** استخراج summary از شکل‌های مختلف پاسخ API */
export const extractAttendanceSummary = (payload) => {
  if (!payload || typeof payload !== 'object') return null;

  const candidates = [
    payload.summary,
    payload.data?.summary,
    payload.data,
    payload,
  ];

  for (const candidate of candidates) {
    if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) continue;
    const nested = candidate.summary;
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      if (SUMMARY_FIELD_KEYS.some((k) => nested[k] != null && nested[k] !== '')) {
        return nested;
      }
    }
    if (SUMMARY_FIELD_KEYS.some((k) => candidate[k] != null && candidate[k] !== '')) {
      return candidate;
    }
  }
  return null;
};

const toFaDigits = (value) => {
  if (value == null) return '—';
  return String(value).replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]);
};

const hasMetricValue = (summary, metric) => {
  if (!summary) return false;
  const primary = summary[metric.valueKey];
  if (primary != null && primary !== '') return true;
  if (metric.fallbackValueKey) {
    const fb = summary[metric.fallbackValueKey];
    if (fb != null && fb !== '' && Number(fb) !== 0) return true;
  }
  return false;
};

const formatMetricValue = (summary, metric) => {
  if (!summary) return '—';
  const raw = summary[metric.valueKey];
  if (raw != null && raw !== '') {
    if (metric.preferHoursLabel) {
      const decimal = summary[metric.decimalKey];
      if (decimal != null && Number.isFinite(Number(decimal))) {
        const n = Number(decimal);
        if (Number.isInteger(n) || Math.abs(n - Math.round(n)) < 0.01) {
          return `${toFaDigits(Math.round(n))} ساعت`;
        }
        return `${toFaDigits(n)} ساعت`;
      }
      const m = String(raw).match(/^(\d+)(?::00)?$/);
      if (m) return `${toFaDigits(m[1])} ساعت`;
    }
    return toFaDigits(raw);
  }

  if (metric.fallbackValueKey) {
    const fb = summary[metric.fallbackValueKey];
    if (fb != null && fb !== '') {
      return `${toFaDigits(fb)}${metric.fallbackSuffix || ''}`;
    }
  }
  return '—';
};

const visibleMetrics = (summary) =>
  METRICS.filter((metric) => !metric.optional || hasMetricValue(summary, metric));

/** نوار فشرده خلاصه ماهانه زیر نام کارمند */
export const AttendanceSummaryStrip = ({ summary, loading = false, className = '' }) => {
  if (loading && !summary) {
    return (
      <div
        className={`text-[11px] text-gray-400 ${className}`}
        style={{ fontFamily: 'CustomFont' }}
      >
        در حال بارگذاری خلاصه…
      </div>
    );
  }

  if (!summary) return null;

  return (
    <div
      className={`flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] leading-5 ${className}`}
      style={{ fontFamily: 'CustomFont' }}
      dir="rtl"
    >
      {visibleMetrics(summary).map((metric) => (
        <span key={metric.key} className="inline-flex items-center gap-1 whitespace-nowrap">
          <span style={{ color: metric.color, fontWeight: 700 }}>{metric.title}:</span>
          <span className="text-gray-800 dark:text-gray-100 tabular-nums font-medium">
            {formatMetricValue(summary, metric)}
          </span>
        </span>
      ))}
      {summary.is_labor_law_set === false && (
        <Tooltip title="تخمین بر اساس شیفت (عدد اداره کار توسط مدیر وارد نشده است)">
          <span className="text-amber-600 text-[11px]">تخمین شیفت</span>
        </Tooltip>
      )}
    </div>
  );
};

/** کارت‌های خلاصه برای صفحه حضور هر پرسنل */
const AttendanceSummaryMetrics = ({ summary, className = '' }) => {
  const normalized = extractAttendanceSummary({ summary }) || extractAttendanceSummary(summary) || summary;
  if (!normalized || typeof normalized !== 'object') return null;
  if (!CORE_SUMMARY_KEYS.some((k) => normalized[k] != null && normalized[k] !== '')) {
    // فقط مأموریت هم کافی است اگر بک‌اند فقط آن را بفرستد
    if (!hasMetricValue(normalized, METRICS.find((m) => m.key === 'mission'))) return null;
  }

  const metrics = visibleMetrics(normalized);
  const cols =
    metrics.length > 6
      ? 'grid-cols-2 md:grid-cols-3 lg:grid-cols-7'
      : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-6';

  return (
    <div className={`grid ${cols} gap-3 mb-4 ${className}`} dir="rtl">
      {metrics.map((metric) => (
        <Card
          key={metric.key}
          size="small"
          className="border-0 shadow-sm"
          styles={{ body: { padding: '12px 14px', background: metric.bg, borderRadius: 8 } }}
        >
          <Statistic
            title={
              <span className="flex items-center gap-1.5" style={{ fontFamily: 'CustomFont', color: metric.color, fontSize: 12 }}>
                {metric.icon}
                {metric.title}
                {metric.key === 'expected' && normalized.is_labor_law_set === false && (
                  <Tooltip title="تخمین بر اساس شیفت (عدد اداره کار توسط مدیر وارد نشده است)">
                    <span className="text-[10px] text-amber-600 font-normal">(تخمین)</span>
                  </Tooltip>
                )}
              </span>
            }
            value={formatMetricValue(normalized, metric)}
            valueStyle={{ fontFamily: 'CustomFont', fontSize: 18, color: '#1f2937', fontWeight: 700 }}
          />
        </Card>
      ))}
    </div>
  );
};

export default AttendanceSummaryMetrics;
